import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Button, Dialog, Input } from '../../ui';

/** One-field dialog (create / rename) validated with zod: 1..max characters after trimming. */
export function NameDialog({
  title,
  label,
  submitLabel,
  initial = '',
  max,
  onSubmit,
  onClose,
}: {
  title: string;
  label: string;
  submitLabel: string;
  initial?: string;
  max: number;
  onSubmit: (value: string) => Promise<void>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const parsed = z.string().trim().min(1).max(max).safeParse(value);
    if (!parsed.success) return setError(t('dashboard.nameLength', { max }));
    setBusy(true);
    try {
      await onSubmit(parsed.data);
      onClose();
    } catch {
      setError(t('auth.genericError'));
      setBusy(false);
    }
  };

  return (
    <Dialog title={title} onClose={onClose}>
      <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
        <label className="flex flex-col gap-1 font-sans text-sm font-medium text-fg">
          {label}
          <Input value={value} autoFocus onChange={(e) => setValue(e.target.value)} />
        </label>
        {error && (
          <p role="alert" className="font-sans text-xs text-danger">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>
            {t('dashboard.cancel')}
          </Button>
          <Button variant="primary" type="submit" disabled={busy}>
            {submitLabel}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
